import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import CalendarPage from "@/pages/CalendarPage";
import SettingsPage from "@/pages/SettingsPage";
import RequestsPage from "@/pages/RequestsPage";
import UsersPage from "@/pages/UsersPage";
import InstructionsPage from "@/pages/InstructionsPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import AbbonamentoPage from "@/pages/AbbonamentoPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ 
    nome_azienda: "La Tua Azienda", 
    logo_base64: null,
    giorno_inizio_settimana: 1,
    giorno_fine_settimana: 5,
    limite_richieste: "tutto_anno",
    limite_mesi: 3,
    chiusura_infrasettimanale_attiva: false,
    chiusura_infrasettimanale_giorno: null,
    chiusura_infrasettimanale_dalle: null,
    chiusura_infrasettimanale_alle: null,
    tema_colore: "blue"
  });

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          // Load settings for authenticated user's company
          const settingsRes = await axios.get(`${API}/settings/company`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSettings(settingsRes.data);
          // Apply theme
          document.documentElement.setAttribute('data-theme', settingsRes.data.tema_colore || 'blue');
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
        }
      } else {
        // Load settings for public pages (login/register)
        try {
          const settingsRes = await axios.get(`${API}/settings`);
          setSettings(settingsRes.data);
          document.documentElement.setAttribute('data-theme', settingsRes.data.tema_colore || 'blue');
        } catch (error) {
          console.error("Failed to load settings");
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    // Load settings for authenticated user's company
    const settingsRes = await axios.get(`${API}/settings/company`, {
      headers: { Authorization: `Bearer ${newToken}` }
    });
    setSettings(settingsRes.data);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const refreshSettings = async () => {
    try {
      const currentToken = localStorage.getItem("token");
      const endpoint = currentToken ? `${API}/settings/company` : `${API}/settings`;
      const config = currentToken ? { headers: { Authorization: `Bearer ${currentToken}` } } : {};
      const settingsRes = await axios.get(endpoint, config);
      setSettings(settingsRes.data);
      // Apply theme
      document.documentElement.setAttribute('data-theme', settingsRes.data.tema_colore || 'blue');
    } catch (error) {
      console.error("Failed to refresh settings");
    }
  };

  const completeOnboarding = async () => {
    try {
      await axios.put(`${API}/auth/complete-onboarding`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(prev => ({ ...prev, onboarding_completato: true }));
    } catch (error) {
      console.error("Failed to complete onboarding");
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error("Failed to refresh user");
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, settings, refreshSettings, completeOnboarding, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
          <Route path="/requests" element={<ProtectedRoute><RequestsPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/abbonamento" element={<ProtectedRoute><AbbonamentoPage /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
          <Route path="/instructions" element={<ProtectedRoute><InstructionsPage /></ProtectedRoute>} />
          <Route path="/super-admin" element={<SuperAdminPage />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
